import React, { useState, useEffect } from "react";
import { useAuth, getAuthToken } from "../context/AuthContext";
import {
  Settings,
  Shield,
  Database,
  Lock,
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  Key,
  Server,
  FileSpreadsheet,
  Building,
  Check,
  X,
  Clock,
  Sparkles
} from "lucide-react";

export const SystemSettingsPage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("readiness");
  const [loading, setLoading] = useState<boolean>(true);

  // Diagnostic states
  const [readinessData, setReadinessData] = useState<any>(null);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [backupsList, setBackupsList] = useState<any[]>([]);
  const [googleStatus, setGoogleStatus] = useState<any>(null);

  // Backup & Restore states
  const [backupLabel, setBackupLabel] = useState<string>("");
  const [creatingBackup, setCreatingBackup] = useState<boolean>(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState<boolean>(false);
  const [selectedBackupForRestore, setSelectedBackupForRestore] = useState<string>("");
  const [restoreConfirmText, setRestoreConfirmText] = useState<string>("");
  const [restoring, setRestoring] = useState<boolean>(false);

  // Password Change state
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [pwdMsg, setPwdMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState<boolean>(false);

  // Status Notification
  const [actionMsg, setActionMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fetchSystemData = async () => {
    setLoading(true);
    try {
      const [readinessRes, settingsRes, backupsRes, googleRes] = await Promise.all([
        fetchWithAuth("/api/system/production-readiness").then(r => r.json()),
        fetchWithAuth("/api/system/settings").then(r => r.json()),
        fetchWithAuth("/api/system/backups").then(r => r.json()),
        fetchWithAuth("/api/system/google-sheets-status").then(r => r.json())
      ]);

      if (readinessRes.success) setReadinessData(readinessRes);
      if (settingsRes.success) setSystemSettings(settingsRes.settings);
      if (backupsRes.success) setBackupsList(backupsRes.backups);
      if (googleRes.success) setGoogleStatus(googleRes);
    } catch (err) {
      console.error("Error loading system settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemData();
  }, []);

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    setActionMsg(null);
    try {
      const res = await fetchWithAuth("/api/system/backup", {
        method: "POST",
        body: JSON.stringify({ label: backupLabel || "manual-ui" })
      }).then(r => r.json());

      if (res.success) {
        setActionMsg({ type: "success", text: `Backup created and verified: ${res.backup.filename}` });
        setBackupLabel("");
        fetchSystemData();
      } else {
        setActionMsg({ type: "error", text: res.message || "Backup failed." });
      }
    } catch (err: any) {
      setActionMsg({ type: "error", text: err.message || "Failed to create backup." });
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (restoreConfirmText !== "RESTORE") {
      alert('Please type "RESTORE" to confirm this database recovery operation.');
      return;
    }

    setRestoring(true);
    setActionMsg(null);
    try {
      const res = await fetchWithAuth("/api/system/restore", {
        method: "POST",
        body: JSON.stringify({ backup_filename: selectedBackupForRestore, confirm: true })
      }).then(r => r.json());

      if (res.success) {
        setActionMsg({
          type: "success",
          text: `Database restored from ${selectedBackupForRestore}. Safety snapshot created: ${res.details.safety_backup}`
        });
        setRestoreModalOpen(false);
        setRestoreConfirmText("");
        fetchSystemData();
      } else {
        setActionMsg({ type: "error", text: res.message || "Restore operation failed." });
      }
    } catch (err: any) {
      setActionMsg({ type: "error", text: err.message || "Failed to restore database." });
    } finally {
      setRestoring(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdMsg(null);

    if (newPassword !== confirmPassword) {
      setPwdMsg({ type: "error", text: "New password and confirmation do not match." });
      return;
    }

    if (newPassword.length < 6) {
      setPwdMsg({ type: "error", text: "Password must be at least 6 characters long." });
      return;
    }

    setChangingPassword(true);
    try {
      const res = await fetchWithAuth("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      }).then(r => r.json());

      if (res.success) {
        setPwdMsg({ type: "success", text: "Password changed successfully." });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPwdMsg({ type: "error", text: res.message || "Failed to change password." });
      }
    } catch (err: any) {
      setPwdMsg({ type: "error", text: err.message || "Error changing password." });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Official Government Header */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#0F2C59] text-white p-6 rounded-2xl shadow-lg border-b-4 border-amber-500">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-wider">
              <Shield className="w-4 h-4" /> District School Education Department, Nuh, Haryana
            </div>
            <h1 className="text-2xl font-black mt-1 tracking-tight flex items-center gap-2">
              <Settings className="w-6 h-6 text-amber-400" />
              System Settings & Production Center
            </h1>
            <p className="text-xs text-blue-100 mt-1 max-w-2xl leading-relaxed">
              Phase 10 Production Hardening • Database Backup & Verification, Security Diagnostics, Password Controls, and Google Sheets Status.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="px-3.5 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-xl text-xs font-black flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4" /> {readinessData?.overall_status || "PRODUCTION READY"}
            </span>
            <button
              onClick={fetchSystemData}
              className="p-2 bg-blue-900/60 hover:bg-blue-800 text-white rounded-xl transition"
              title="Refresh Diagnostics"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mt-6 overflow-x-auto pt-2 border-t border-blue-800/60">
          {[
            { id: "readiness", label: "Production Readiness", icon: Shield },
            { id: "backup", label: "Backup & Restore", icon: Database },
            { id: "security", label: "Security & Password", icon: Lock },
            { id: "info", label: "System Information", icon: Server }
          ].map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition whitespace-nowrap ${
                  active
                    ? "bg-amber-500 text-slate-950 font-black shadow-md"
                    : "bg-blue-900/40 text-blue-100 hover:bg-blue-800/60 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {actionMsg && (
        <div
          className={`p-4 rounded-xl text-xs font-bold flex items-center gap-2 ${
            actionMsg.type === "success"
              ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
              : "bg-rose-50 text-rose-900 border border-rose-200"
          }`}
        >
          {actionMsg.type === "success" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}
          {actionMsg.text}
        </div>
      )}

      {/* TAB 1: PRODUCTION READINESS DIAGNOSTIC */}
      {activeTab === "readiness" && readinessData && (
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-black text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-900" />
                  Government Portal Production Readiness Audit
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Automated validation of all production security, database, and system sub-modules.</p>
              </div>
              <div className="text-right">
                <span className="text-[11px] font-bold text-slate-400">Last Verified: {new Date(readinessData.timestamp).toLocaleTimeString("en-IN")}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-2">
              {readinessData.checks?.map((check: any) => (
                <div key={check.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-800">{check.name}</span>
                    <span
                      className={`px-2.5 py-0.5 rounded text-[10px] font-black uppercase ${
                        check.status === "READY"
                          ? "bg-emerald-100 text-emerald-900"
                          : check.status === "WARNING"
                          ? "bg-amber-100 text-amber-900"
                          : "bg-rose-100 text-rose-900"
                      }`}
                    >
                      {check.status}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 font-medium leading-relaxed">{check.details}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: BACKUP & RESTORE CENTER */}
      {activeTab === "backup" && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-black text-slate-900 flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-900" />
                  Database Backup & Recovery Center
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Generate verified timestamped backups with automated SQLite integrity check. Pre-restore safety snapshots protect against data loss.
                </p>
              </div>
            </div>

            {/* Create Backup Form */}
            <div className="p-4 bg-blue-50/60 border border-blue-200 rounded-xl flex flex-wrap items-center justify-between gap-4">
              <div className="flex-1 min-w-[240px]">
                <label className="text-xs font-bold text-blue-950 block mb-1">Optional Backup Label</label>
                <input
                  type="text"
                  value={backupLabel}
                  onChange={e => setBackupLabel(e.target.value)}
                  placeholder="e.g. pre-board-exam-snapshot"
                  className="w-full px-3 py-1.5 bg-white border border-blue-300 rounded-lg text-xs font-medium text-slate-800"
                />
              </div>
              <button
                onClick={handleCreateBackup}
                disabled={creatingBackup}
                className="px-4 py-2.5 bg-blue-900 hover:bg-blue-950 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition disabled:opacity-50"
              >
                <Download className="w-4 h-4 text-amber-400" />
                {creatingBackup ? "Creating & Verifying..." : "Create Verified Backup"}
              </button>
            </div>

            {/* Backups List */}
            <div>
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Available Database Backups ({backupsList.length})</h3>
              <table className="w-full text-left text-xs border border-slate-200 border-collapse">
                <thead className="bg-slate-100 font-bold text-slate-700">
                  <tr>
                    <th className="p-2.5 border">Filename</th>
                    <th className="p-2.5 border text-center">Size</th>
                    <th className="p-2.5 border text-center">Integrity Status</th>
                    <th className="p-2.5 border text-right">Created At</th>
                    <th className="p-2.5 border text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backupsList.map(b => (
                    <tr key={b.filename} className="hover:bg-slate-50">
                      <td className="p-2.5 border font-mono font-bold text-slate-900">
                        {b.filename}
                        {b.is_pre_restore && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded text-[9px] font-black uppercase">
                            Safety Snapshot
                          </span>
                        )}
                      </td>
                      <td className="p-2.5 border text-center text-slate-600 font-medium">{(b.size / 1024).toFixed(1)} KB</td>
                      <td className="p-2.5 border text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${b.verified ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`}>
                          {b.integrity_status}
                        </span>
                      </td>
                      <td className="p-2.5 border text-right text-slate-500">{new Date(b.created_at).toLocaleString("en-IN")}</td>
                      <td className="p-2.5 border text-center">
                        <button
                          onClick={() => {
                            setSelectedBackupForRestore(b.filename);
                            setRestoreModalOpen(true);
                          }}
                          className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded text-[10px] transition"
                        >
                          Safe Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: SECURITY & PASSWORD */}
      {activeTab === "security" && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs max-w-xl space-y-4">
          <h2 className="text-base font-black text-slate-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-900" />
            Security & Account Password Management
          </h2>
          <p className="text-xs text-slate-500">Update your login password securely using PBKDF2 cryptography.</p>

          {pwdMsg && (
            <div
              className={`p-3 rounded-lg text-xs font-bold ${
                pwdMsg.type === "success" ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-rose-50 text-rose-900 border border-rose-200"
              }`}
            >
              {pwdMsg.text}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">New Password (min 6 characters)</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs"
              />
            </div>
            <button
              type="submit"
              disabled={changingPassword}
              className="px-4 py-2 bg-blue-900 hover:bg-blue-950 text-white rounded-xl text-xs font-bold shadow-sm transition disabled:opacity-50"
            >
              {changingPassword ? "Updating Password..." : "Change Password"}
            </button>
          </form>
        </div>
      )}

      {/* TAB 4: SYSTEM INFORMATION */}
      {activeTab === "info" && systemSettings && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <h2 className="text-base font-black text-slate-900 flex items-center gap-2">
            <Server className="w-5 h-5 text-blue-900" />
            Government School Application Profile
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-medium text-slate-700 pt-2">
            <div className="p-3 bg-slate-50 rounded-xl border">Application: <strong className="text-slate-900">{systemSettings.application_name}</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">Department: <strong className="text-slate-900">{systemSettings.department}</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">School: <strong className="text-slate-900">{systemSettings.school_name} ({systemSettings.school_code})</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">Academic Session: <strong className="text-blue-900">{systemSettings.academic_session}</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">Database Mode: <strong className="text-emerald-700">{systemSettings.database_status}</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">Google Sheets: <strong className="text-slate-900">{systemSettings.google_sheets_status}</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">Environment: <strong className="text-slate-900">{systemSettings.node_env} (Port {systemSettings.port})</strong></div>
            <div className="p-3 bg-slate-50 rounded-xl border">Build Version: <strong className="text-slate-900">{systemSettings.app_version}</strong></div>
          </div>
        </div>
      )}

      {/* RESTORE CONFIRMATION MODAL */}
      {restoreModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-2 text-rose-600 font-black text-sm">
              <AlertTriangle className="w-5 h-5" />
              Confirm Database Restore
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              You are about to restore the database from: <strong className="font-mono text-slate-900">{selectedBackupForRestore}</strong>.
              A pre-restore safety snapshot will be automatically created before restoration.
            </p>
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-900 font-medium">
              To confirm, type <strong className="font-mono font-bold">RESTORE</strong> below:
            </div>
            <input
              type="text"
              value={restoreConfirmText}
              onChange={e => setRestoreConfirmText(e.target.value)}
              placeholder="Type RESTORE"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs font-mono font-bold"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => { setRestoreModalOpen(false); setRestoreConfirmText(""); }}
                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleRestoreBackup}
                disabled={restoreConfirmText !== "RESTORE" || restoring}
                className="px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold disabled:opacity-50"
              >
                {restoring ? "Restoring..." : "Confirm & Restore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
